import React, { useEffect, useState } from "react";
import axios from "axios";
import { Marker, Polyline, Tooltip } from "react-leaflet";
import { Spin } from "antd";
import {
  createCurrentLocationMarker,
  createStationaryIcon,
  getRouteDeviation,
} from "../../../../Utils/getMapData";

// =========================================================
// HELPERS
// =========================================================

const getSegmentKey = (segment) =>
  segment.map(([lat, lng]) => `${lat},${lng}`).join("|");

// =========================================================
// FALLBACK: BUILD ROAD-BASED PLANNED ROUTE
// =========================================================

const getFastestRoute = async (stopCoordinates) => {
  if (!Array.isArray(stopCoordinates) || stopCoordinates.length < 2) {
    return stopCoordinates || [];
  }

  try {
    const routeCoordinates = [...stopCoordinates];

    // -------------------------------------------------------
    // Round-trip support
    //
    // If the final stop isn't the first station,
    // append the first point so OSRM calculates:
    //
    // last stop -> station
    // -------------------------------------------------------

    const firstPoint = routeCoordinates[0];
    const lastPoint = routeCoordinates[routeCoordinates.length - 1];

    const sameStartAndEnd =
      firstPoint &&
      lastPoint &&
      Number(firstPoint[0]) === Number(lastPoint[0]) &&
      Number(firstPoint[1]) === Number(lastPoint[1]);

    if (!sameStartAndEnd) {
      routeCoordinates.push([Number(firstPoint[0]), Number(firstPoint[1])]);
    }

    // -------------------------------------------------------
    // Remove invalid coordinates
    // -------------------------------------------------------

    const validCoordinates = routeCoordinates.filter(
      (point) =>
        Array.isArray(point) &&
        point.length >= 2 &&
        Number.isFinite(Number(point[0])) &&
        Number.isFinite(Number(point[1])),
    );

    if (validCoordinates.length < 2) {
      return stopCoordinates;
    }

    // -------------------------------------------------------
    // OSRM expects:
    //
    // lng,lat;lng,lat;lng,lat
    // -------------------------------------------------------

    const coordinates = validCoordinates
      .map(([lat, lng]) => `${lng},${lat}`)
      .join(";");

    const response = await axios.get(
      `https://router.project-osrm.org/route/v1/driving/${coordinates}`,
      {
        params: {
          overview: "full",
          geometries: "geojson",
          steps: false,
        },
      },
    );

    const route = response?.data?.routes?.[0]?.geometry?.coordinates;

    if (!Array.isArray(route) || route.length < 2) {
      return stopCoordinates;
    }

    // OSRM -> [lng, lat]
    // Leaflet -> [lat, lng]

    return route.map(([lng, lat]) => [Number(lat), Number(lng)]);
  } catch (error) {
    console.error("Failed to create planned route:", error);

    return stopCoordinates;
  }
};

// =========================================================
// SNAP ACTUAL GPS ROUTE
// =========================================================

const snapCache = new Map();

export const snapGpsRoute = async (actualRoute) => {
  if (!Array.isArray(actualRoute)) {
    return [];
  }

  const snappedRoute = await Promise.all(
    actualRoute.map(async (point) => {
      const lat = Array.isArray(point) ? Number(point[0]) : Number(point?.lat);

      const lng = Array.isArray(point)
        ? Number(point[1])
        : Number(point?.lng ?? point?.long);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return null;
      }

      const key = `${lat},${lng}`;

      // -------------------------------------------------------
      // CACHE
      // -------------------------------------------------------

      if (snapCache.has(key)) {
        return snapCache.get(key);
      }

      try {
        const response = await axios.get(
          `https://router.project-osrm.org/nearest/v1/driving/${lng},${lat}`,
        );

        if (response?.data?.code === "Ok") {
          const [snappedLng, snappedLat] =
            response.data.waypoints?.[0]?.location || [];

          if (Number.isFinite(snappedLat) && Number.isFinite(snappedLng)) {
            const snappedPoint = [snappedLat, snappedLng];

            snapCache.set(key, snappedPoint);

            return snappedPoint;
          }
        }
      } catch (error) {
        console.error("GPS snap failed:", error);
      }

      // -------------------------------------------------------
      // FALLBACK TO ORIGINAL GPS POINT
      // -------------------------------------------------------

      const originalPoint = [lat, lng];

      snapCache.set(key, originalPoint);

      return originalPoint;
    }),
  );

  return snappedRoute.filter(Boolean);
};

// =========================================================
// COMPONENT
// =========================================================

const OngoingTripMapComponents = ({
  stopCoordinates = [],
  fastestRouteCoordinates = [],
  actualRoute = [],
  stationaryPoints = [],
}) => {
  const [plannedRoute, setPlannedRoute] = useState([]);

  const [gpsRoute, setGpsRoute] = useState([]);

  const [routeLoading, setRouteLoading] = useState(true);

  // =========================================================
  // PLANNED ROUTE
  //
  // 1. Use route stored in DB.
  // 2. If DB route doesn't exist, generate using OSRM.
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadPlannedRoute = async () => {
      setRouteLoading(true);

      // -----------------------------------------------------
      // FIRST: USE STORED ROUTE
      // -----------------------------------------------------

      const hasStoredRoute =
        Array.isArray(fastestRouteCoordinates) &&
        fastestRouteCoordinates.length >= 2;

      if (hasStoredRoute) {
        if (!cancelled) {
          setPlannedRoute(fastestRouteCoordinates);

          setRouteLoading(false);
        }

        return;
      }

      // -----------------------------------------------------
      // FALLBACK: GENERATE ROUTE
      // -----------------------------------------------------

      try {
        const generatedRoute = await getFastestRoute(stopCoordinates);

        if (cancelled) {
          return;
        }

        setPlannedRoute(generatedRoute || []);
      } catch (error) {
        console.error("Planned route error:", error);

        if (!cancelled) {
          setPlannedRoute(stopCoordinates || []);
        }
      } finally {
        if (!cancelled) {
          setRouteLoading(false);
        }
      }
    };

    loadPlannedRoute();

    return () => {
      cancelled = true;
    };
  }, [stopCoordinates, fastestRouteCoordinates]);

  // =========================================================
  // PROCESS ACTUAL GPS ROUTE
  //
  // This remains separate from planned route generation.
  // =========================================================

  useEffect(() => {
    let cancelled = false;

    const loadGpsRoute = async () => {
      if (!actualRoute?.length) {
        setGpsRoute([]);
        return;
      }

      try {
        const normalizedRoute = actualRoute
          .map((point) => {
            const lat = Array.isArray(point)
              ? Number(point[0])
              : Number(point?.lat);

            const lng = Array.isArray(point)
              ? Number(point[1])
              : Number(point?.lng ?? point?.long);

            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              return null;
            }

            return [lat, lng];
          })
          .filter(Boolean);

        const snappedRoute = await snapGpsRoute(normalizedRoute);

        if (!cancelled) {
          setGpsRoute(snappedRoute);
        }
      } catch (error) {
        console.error("Actual route processing error:", error);

        if (!cancelled) {
          setGpsRoute(actualRoute || []);
        }
      }
    };

    loadGpsRoute();

    return () => {
      cancelled = true;
    };
  }, [actualRoute]);

  // =========================================================
  // DEVIATION
  //
  // IMPORTANT:
  //
  // Compare GPS against the ROAD-BASED planned route.
  //
  // Not against raw stopCoordinates.
  // =========================================================

  const {
    normalSegments = [],
    plannedSegments = [],
    actualSegments = [],
    ongoingActualSegment = null,
    lastMatchedPlanned = -1,
  } = getRouteDeviation(plannedRoute, gpsRoute, 40);

  // =========================================================
  // REMAINING PLANNED ROAD
  //
  // Because plannedRoute is actual OSRM road geometry,
  // remaining route will also stay on roads.
  // =========================================================

  const remainingRoute =
    lastMatchedPlanned >= 0
      ? plannedRoute.slice(Math.max(0, lastMatchedPlanned))
      : plannedRoute;

  // =========================================================
  // LOADING
  // =========================================================

  if (routeLoading) {
    return (
      <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-white/40">
        <Spin size="large" />
      </div>
    );
  }

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <>
      {/* =====================================================
          TRAVELLED / NORMAL ROUTE
          ===================================================== */}

      {normalSegments.map((segment) => (
        <React.Fragment key={`normal-${getSegmentKey(segment)}`}>
          <Polyline
            positions={segment}
            pathOptions={{
              color: "#085054",
              weight: 8,
              lineCap: "round",
              lineJoin: "round",
            }}
          />

          <Polyline
            positions={segment}
            pathOptions={{
              color: "#AABDBD",
              weight: 5,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </React.Fragment>
      ))}

      {/* =====================================================
          PLANNED / SKIPPED ROUTE
          ===================================================== */}

      {plannedSegments.map(({ coordinates }) => (
        <React.Fragment key={`planned-${getSegmentKey(coordinates)}`}>
          <Polyline
            positions={coordinates}
            pathOptions={{
              color: "#528587",
              weight: 6,
              dashArray: "10 15",
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </React.Fragment>
      ))}

      {/* =====================================================
          ACTUAL DEVIATION
          ===================================================== */}

      {actualSegments.map((segment) => (
        <React.Fragment key={`actual-${getSegmentKey(segment)}`}>
          <Polyline
            positions={segment}
            pathOptions={{
              color: "#C84630",
              weight: 8,
              lineCap: "round",
              lineJoin: "round",
            }}
          />

          <Polyline
            positions={segment}
            pathOptions={{
              color: "#F4DAD6",
              weight: 5,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </React.Fragment>
      ))}

      {/* =====================================================
          REMAINING PLANNED ROUTE
          ===================================================== */}

      {remainingRoute.length >= 2 && (
        <Polyline
          positions={remainingRoute}
          pathOptions={{
            color: "#528587",
            weight: 6,
            dashArray: "10 15",
            lineCap: "round",
            lineJoin: "round",
          }}
        />
      )}

      {/* =====================================================
          CURRENT / ONGOING DEVIATION
          ===================================================== */}

      {ongoingActualSegment?.length >= 2 && (
        <>
          <Polyline
            positions={ongoingActualSegment}
            pathOptions={{
              color: "#C84630",
              weight: 8,
              lineCap: "round",
              lineJoin: "round",
            }}
          />

          <Polyline
            positions={ongoingActualSegment}
            pathOptions={{
              color: "#F4DAD6",
              weight: 5,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        </>
      )}

      {/* =====================================================
          CURRENT VEHICLE POSITION
          ===================================================== */}

      {gpsRoute.length > 0 && (
        <Marker
          position={gpsRoute[gpsRoute.length - 1]}
          icon={createCurrentLocationMarker({
            isDeviated:
              actualSegments.length > 0 || Boolean(ongoingActualSegment),
          })}
        />
      )}

      {/* =====================================================
          STATIONARY POINTS
          ===================================================== */}

      {stationaryPoints?.map((point) => (
        <Marker
          key={`stationary-${point.lat}-${point.long}-${point.duration_seconds}`}
          position={[point.lat, point.long]}
          icon={createStationaryIcon()}
        >
          <Tooltip
            permanent={false}
            direction="top"
            offset={[0, -10]}
            opacity={1}
          >
            <div className="w-[229px] rounded-md p-2">
              <div className="flex justify-between">
                <span className="font-aileron font-normal text-xs text-dark-gray">
                  Idle Time Detected
                </span>

                <span className="rounded-4 bg-light-grey px-2 py-1 font-aileron font-semibold text-xs text-black">
                  {Math.round(point.duration_seconds / 60)} Min
                </span>
              </div>

              <div className="mt-2 flex justify-between">
                <span className="font-aileron font-normal text-xs text-dark-gray">
                  Location
                </span>

                <span className="font-aileron font-semibold text-xs text-black">
                  {Number(point.lat).toFixed(6)},{" "}
                  {Number(point.long).toFixed(6)}
                </span>
              </div>
            </div>
          </Tooltip>
        </Marker>
      ))}
    </>
  );
};

export default OngoingTripMapComponents;
